import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { 
  Upload, BadgeCheck, Store, Loader2, Truck,
  Check, AlertCircle, Info, Building2, CreditCard, Phone, Mail, MapPin, ShieldCheck, FileCheck, Trash2, Plus
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Link } from 'react-router-dom';
import VendorRazorpayRouteCard from '../../components/vendor/VendorRazorpayRouteCard';
import { StoreWideLicensesCard } from '../../components/vendor/StoreWideLicensesCard';
import { LICENSE_TYPE_OPTIONS, LICENSE_TYPE_LABELS } from '../../constants/compliance';

const VendorStoreProfile = () => {
  const { user, apiCall } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  
  // Store profile editable fields
  const [storeProfile, setStoreProfile] = useState({
    store_name: '',
    store_handle: '',
    tagline: '',
    about_us: '',
    store_logo: '',
    banner_image: '',
    customer_email: '',
    customer_phone: '',
    business_address: '',
    operating_hours: 'Mon-Fri: 9:00 AM - 5:00 PM',
    delivery_mode_default: 'platform_managed',
    // iter-231-K — vendor-level processing/handling time. Resolver
    // falls back to (1, 2) when these are blank. Surfaces in the PDP
    // Shipping Modal as "Dispatched in X–Y days, N days in transit".
    processing_days_min: '',
    processing_days_max: ''
  });

  // Auto-filled data from registration (read-only display)
  const [registrationData, setRegistrationData] = useState({
    // From user account
    registered_email: '',
    registered_phone: '',
    // From GST/Tax registration
    gst_legal_name: '',
    gst_trade_name: '',
    gst_business_address: '',
    gst_state: '',
    pan_number: '',
    gstin: '',
    // From bank verification
    bank_name: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_account_holder: '',
    bank_verified: false
  });

  // iter-325 — Naming Policy status + Shipping Management mode
  // (read-only info; Shipping is managed on the dedicated Vendor
  // Shipping Setup page — see /vendor/shipping).
  const [namingPolicy, setNamingPolicy] = useState({ accepted: false, accepted_at: null });
  const [namingPolicySaving, setNamingPolicySaving] = useState(false);
  const [shippingMode, setShippingMode] = useState('aniyor_managed');

  // iter-350 — Saved license numbers (remembered per type across listings)
  const [savedLicenses, setSavedLicenses] = useState({});
  const [forgettingType, setForgettingType] = useState(null);

  // iter-374 — Store-wide license/certification defaults (auto-applied
  // to every listing unless explicitly excluded on that listing).
  const [vendorLicenses, setVendorLicenses] = useState([]);
  const [licensesSaving, setLicensesSaving] = useState(false);
  const [uploadingLicenseIdx, setUploadingLicenseIdx] = useState(null);
  const [verifyingLicenseIdx, setVerifyingLicenseIdx] = useState(null);

  useEffect(() => {
    fetchAllData();
    fetchSavedLicenses();
    fetchVendorLicenses();
  }, []);

  const fetchSavedLicenses = async () => {
    try {
      const res = await apiCall('/api/listings/defaults/licenses');
      setSavedLicenses(res?.default_licenses || {});
    } catch (e) { /* non-critical */ }
  };

  const fetchVendorLicenses = async () => {
    try {
      const res = await apiCall('/api/vendors/profile/licenses');
      setVendorLicenses(res?.licenses || []);
    } catch (e) { /* non-critical */ }
  };

  const updateVendorLicense = (idx, patch) => {
    setVendorLicenses((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const addVendorLicenseRow = () => {
    setVendorLicenses((prev) => [...prev, { type: 'FSSAI', number: '', image_url: '' }]);
  };

  const removeVendorLicenseRow = (idx) => {
    setVendorLicenses((prev) => prev.filter((_, i) => i !== idx));
  };

  const uploadVendorLicenseImage = async (idx, file) => {
    setUploadingLicenseIdx(idx);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const token = sessionStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/media/upload?optimize=false`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: uploadFormData,
      });
      if (response.ok) {
        const data = await response.json();
        updateVendorLicense(idx, { image_url: data.cdn_url || data.file_url || data.url });
      }
    } finally {
      setUploadingLicenseIdx(null);
    }
  };

  const handleVerifyLicense = async (idx, renewalConfirmed = false) => {
    setVerifyingLicenseIdx(idx);
    try {
      const res = await apiCall(`/api/vendors/profile/licenses/${idx}/verify`, {
        method: 'POST',
        body: JSON.stringify({ renewal_confirmed: renewalConfirmed }),
      });
      if (res.license) updateVendorLicense(idx, res.license);
      switch (res.outcome) {
        case 'verified':
          toast({ title: 'Verified', description: `FSSAI license verified — expires ${res.license?.expiry_date || 'N/A'}.` });
          break;
        case 'already_verified':
          toast({ title: 'Already Verified', description: res.message || 'This FSSAI license is already verified — no need to re-verify.' });
          break;
        case 'needs_confirmation':
          toast({ title: 'Confirm Renewal', description: res.message || 'Please confirm you have renewed this license, then re-verify.' });
          break;
        case 'blocked_attempts':
          toast({ title: 'Verification Limit Reached', description: res.message, variant: 'destructive' });
          break;
        case 'blocked_concurrent':
          toast({ title: 'Please Wait', description: res.message, variant: 'destructive' });
          break;
        case 'technical_failure':
          toast({ title: 'Verifico Unavailable', description: res.message || 'Verification service is temporarily unavailable — your attempt was not counted. Please try again shortly.', variant: 'destructive' });
          break;
        default:
          toast({ title: 'Verification Failed', description: res.message || res.license?.last_verification_error || 'Could not verify this license.', variant: 'destructive' });
      }
    } catch (error) {
      const msg = error.message || '';
      if (msg.toLowerCase().includes('out of range')) {
        toast({ title: 'Save Required', description: 'Please click "Save Licenses" first, then verify this entry.', variant: 'destructive' });
      } else {
        toast({ title: 'Error', description: msg || 'Failed to verify license', variant: 'destructive' });
      }
    } finally {
      setVerifyingLicenseIdx(null);
    }
  };

  const handleSaveVendorLicenses = async () => {
    setLicensesSaving(true);
    try {
      const res = await apiCall('/api/vendors/profile/licenses', {
        method: 'PUT',
        body: JSON.stringify({
          licenses: vendorLicenses.map((l) => ({ type: l.type, number: l.number, image_url: l.image_url })),
        }),
      });
      setVendorLicenses(res?.licenses || []);
      toast({
        title: 'Success',
        description: `Store-wide licenses updated. ${res?.listings_resynced || 0} listing(s) auto-synced.`,
      });
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to update licenses', variant: 'destructive' });
    } finally {
      setLicensesSaving(false);
    }
  };

  const handleForgetLicense = async (licenseType) => {
    setForgettingType(licenseType);
    try {
      const res = await apiCall(`/api/listings/defaults/licenses/${licenseType}`, { method: 'DELETE' });
      setSavedLicenses(res?.default_licenses || {});
      toast({ title: 'Removed', description: `${licenseType} number forgotten. New listings will ask for it again.` });
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to remove saved license', variant: 'destructive' });
    } finally {
      setForgettingType(null);
    }
  };

  const fetchAllData = async () => {
    setDataLoading(true);
    try {
      // Fetch complete profile data from vendor endpoint
      const [unifiedProfile, verificationData] = await Promise.all([
        apiCall(`/api/vendors/complete-profile/me`).catch(() => null),
        apiCall('/api/brand-registry/verification/status').catch(() => null)
      ]);
      
      // Extract data from unified profile
      const identity = unifiedProfile?.identity || {};
      const store = unifiedProfile?.store || {};
      const business = unifiedProfile?.business || {};
      const bank = unifiedProfile?.bank || {};
      const contact = unifiedProfile?.contact || {};
      const addresses = unifiedProfile?.addresses || {};
      const userInfo = unifiedProfile?.user || {};
      const vp = unifiedProfile?.vendor_profile || {};
      const agreements = unifiedProfile?.agreements || {};
      const shipping = unifiedProfile?.shipping || {};

      setNamingPolicy({
        accepted: !!agreements.naming_policy_accepted,
        accepted_at: agreements.naming_policy_accepted_at || null,
      });
      setShippingMode(shipping.shipping_mode || 'aniyor_managed');
      
      // Set registration data (auto-filled, read-only)
      setRegistrationData({
        // User account data
        registered_email: contact?.primary_email || userInfo?.email || '',
        registered_phone: contact?.primary_mobile || userInfo?.mobile || '',
        // GST/Tax data
        gst_legal_name: business?.legal_name || '',
        gst_trade_name: business?.trade_name || '',
        gst_business_address: addresses?.business?.full_address || business?.gst_principal_address || '',
        gst_state: addresses?.business?.state || '',
        pan_number: business?.pan_number || '',
        gstin: business?.gstin || '',
        // Bank data
        bank_name: bank?.bank_name || '',
        bank_account_number: bank?.account_number || '',
        bank_ifsc: bank?.ifsc_code || '',
        bank_account_holder: bank?.account_holder_name || '',
        bank_verified: bank?.bank_verified || false
      });
      
      // Set editable store profile
      // Priority: identity.store_name (from Store Identity step) > store.store_name > business.trade_name
      setStoreProfile({
        store_name: identity?.store_name || store?.store_name || business?.trade_name || '',
        store_handle: identity?.store_handle || store?.store_handle || '',
        tagline: store?.tagline || '',
        about_us: store?.about_us || '',
        store_logo: identity?.store_logo || store?.store_logo || '',
        banner_image: store?.banner_image || '',
        customer_email: contact?.support_email || contact?.primary_email || '',
        customer_phone: contact?.support_mobile || contact?.primary_mobile || '',
        business_address: addresses?.pickup?.full_address || addresses?.business?.full_address || '',
        operating_hours: store?.operating_hours || 'Mon-Fri: 9:00 AM - 5:00 PM',
        delivery_mode_default: store?.delivery_mode_default || vp?.delivery_mode_default || 'platform_managed',
        // iter-231-K — Read from either the canonical business_details
        // path (admin tooling) or the flat vendor_profile path (this UI's
        // own save format). Backend resolver honors both.
        processing_days_min: (
          vp?.processing_days_min ??
          vp?.business_details?.processing_days_min ??
          ''
        ),
        processing_days_max: (
          vp?.processing_days_max ??
          vp?.business_details?.processing_days_max ??
          ''
        )
      });

      setVerificationStatus(verificationData);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profile data',
        variant: 'destructive'
      });
    } finally {
      setDataLoading(false);
    }
  };

  // iter-325 — Naming Policy toggle: updates the existing vendor record
  // directly, does NOT touch store_name/slug, never re-triggers the
  // registration flow.
  const handleToggleNamingPolicy = async (nextValue) => {
    setNamingPolicySaving(true);
    try {
      const res = await apiCall('/api/brand-registry/naming-policy', {
        method: 'PUT',
        body: JSON.stringify({ accepted: nextValue }),
      });
      setNamingPolicy({ accepted: !!res.naming_policy_accepted, accepted_at: res.naming_policy_accepted_at || null });
      toast({
        title: nextValue ? 'Naming Policy Accepted' : 'Naming Policy Un-accepted',
        description: nextValue ? 'Thanks for confirming — you can keep using your current store name.' : 'Marked as not accepted.',
      });
    } catch (err) {
      toast({ title: 'Error', description: err.message || 'Failed to update Naming Policy status', variant: 'destructive' });
    } finally {
      setNamingPolicySaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // iter-231-K — Coerce processing-days strings → ints (or null when
      // blank, so the resolver falls back to the platform default).
      const _coerceDays = (v) => {
        if (v === '' || v === null || v === undefined) return null;
        const n = parseInt(v, 10);
        return Number.isFinite(n) && n >= 0 ? n : null;
      };
      const payload = {
        ...storeProfile,
        processing_days_min: _coerceDays(storeProfile.processing_days_min),
        processing_days_max: _coerceDays(storeProfile.processing_days_max),
      };
      await apiCall(`/api/users/me`, {
        method: 'PUT',
        body: JSON.stringify({
          vendor_profile: payload
        })
      });
      toast({ title: 'Success', description: 'Store profile updated successfully' });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to update profile',
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const hasBlueTick = verificationStatus?.badges?.is_brand_owner;
  const hasGreenTick = verificationStatus?.badges?.is_legacy_store;

  // Mask sensitive data for display
  const maskAccountNumber = (num) => {
    if (!num) return '';
    const str = String(num);
    if (str.length <= 4) return str;
    return '*'.repeat(str.length - 4) + str.slice(-4);
  };

  if (dataLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div>
          <h1 className="text-xl md:text-3xl font-bold">Store Profile</h1>
          <p className="text-gray-500 mt-1">Manage your store&apos;s public identity and contact information</p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Public Identity Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Public Identity
                {hasBlueTick && (
                  <Badge className="bg-blue-500 text-white gap-1">
                    <BadgeCheck className="h-3 w-3" /> Blue Tick
                  </Badge>
                )}
                {hasGreenTick && (
                  <Badge className="bg-green-500 text-white gap-1">
                    <Store className="h-3 w-3" /> Green Tick
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>How your store appears to customers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="store_name" className="flex items-center gap-2">
                    Store Name / Display Name *
                    {registrationData.gst_trade_name && storeProfile.store_name === registrationData.gst_trade_name && (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                        <Check className="h-3 w-3 mr-1" />
                        From GST
                      </Badge>
                    )}
                  </Label>
                  <Input
                    id="store_name"
                    value={storeProfile.store_name}
                    onChange={(e) => setStoreProfile({ ...storeProfile, store_name: e.target.value })}
                    placeholder="My Amazing Store"
                    required
                  />
                  {registrationData.gst_trade_name && storeProfile.store_name !== registrationData.gst_trade_name && (
                    <p className="text-xs text-gray-500">
                      GST Trade Name: {registrationData.gst_trade_name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="store_handle">Store URL Handle</Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      aniyor.in/store/
                    </span>
                    <Input
                      id="store_handle"
                      value={storeProfile.store_handle}
                      onChange={(e) => setStoreProfile({ ...storeProfile, store_handle: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      placeholder="mystore"
                      className="rounded-l-none"
                      disabled
                    />
                  </div>
                  <p className="text-xs text-gray-500">Store URL is locked after registration. Contact support to change.</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tagline">Short Description / Tagline</Label>
                <Input
                  id="tagline"
                  value={storeProfile.tagline}
                  onChange={(e) => setStoreProfile({ ...storeProfile, tagline: e.target.value })}
                  placeholder="Your one-stop shop for quality products"
                  maxLength={150}
                />
                <p className="text-xs text-gray-500">{storeProfile.tagline?.length || 0}/150 characters</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="about_us">Details About Us</Label>
                <Textarea
                  id="about_us"
                  value={storeProfile.about_us}
                  onChange={(e) => setStoreProfile({ ...storeProfile, about_us: e.target.value })}
                  placeholder="Tell customers about your store, values, and what makes you unique..."
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Visual Branding Card */}
          <Card>
            <CardHeader>
              <CardTitle>Visual Branding</CardTitle>
              <CardDescription>Upload your store logo and banner</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Store Logo</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-blue-400 transition-colors cursor-pointer">
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload logo</p>
                    <p className="text-xs text-gray-500">Recommended: 200x200px</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Banner Image</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-blue-400 transition-colors cursor-pointer">
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload banner</p>
                    <p className="text-xs text-gray-500">Recommended: 1200x400px</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information Card - Auto-filled */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                Contact Information
              </CardTitle>
              <CardDescription>How customers can reach you - Pre-filled from your registration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customer_email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    Customer Support Email
                    {storeProfile.customer_email === registrationData.registered_email && registrationData.registered_email && (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                        <Check className="h-3 w-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </Label>
                  <Input
                    id="customer_email"
                    type="email"
                    value={storeProfile.customer_email}
                    onChange={(e) => setStoreProfile({ ...storeProfile, customer_email: e.target.value })}
                    placeholder="support@mystore.com"
                  />
                  {registrationData.registered_email && (
                    <p className="text-xs text-gray-500">
                      Registered: {registrationData.registered_email}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer_phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    Customer Support Phone
                    {storeProfile.customer_phone === registrationData.registered_phone && registrationData.registered_phone && (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                        <Check className="h-3 w-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </Label>
                  <Input
                    id="customer_phone"
                    value={storeProfile.customer_phone}
                    onChange={(e) => setStoreProfile({ ...storeProfile, customer_phone: e.target.value })}
                    placeholder="+91 98765 43210"
                  />
                  {registrationData.registered_phone && (
                    <p className="text-xs text-gray-500">
                      Registered: {registrationData.registered_phone}
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="business_address" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  Business Address
                  {storeProfile.business_address === registrationData.gst_business_address && registrationData.gst_business_address && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                      <Check className="h-3 w-3 mr-1" />
                      From GST
                    </Badge>
                  )}
                </Label>
                <Textarea
                  id="business_address"
                  value={storeProfile.business_address}
                  onChange={(e) => setStoreProfile({ ...storeProfile, business_address: e.target.value })}
                  placeholder="123 Main Street, City, State - 123456"
                  rows={2}
                />
                {registrationData.gst_business_address && storeProfile.business_address !== registrationData.gst_business_address && (
                  <p className="text-xs text-gray-500">
                    GST Address: {registrationData.gst_business_address}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="operating_hours">Operating Hours</Label>
                <Input
                  id="operating_hours"
                  value={storeProfile.operating_hours}
                  onChange={(e) => setStoreProfile({ ...storeProfile, operating_hours: e.target.value })}
                  placeholder="Mon-Fri: 9:00 AM - 5:00 PM"
                />
              </div>
            </CardContent>
          </Card>

          {/* Saved Licenses & Certificates Card (iter-350) */}
          <Card data-testid="saved-licenses-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5" />
                Saved Licenses &amp; Certificates
              </CardTitle>
              <CardDescription>
                We remember license numbers you enter (FSSAI, BIS, CDSCO, etc.) and pre-fill them on your future
                listings so you don't have to re-type them. Remove one below if the number ever changes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.keys(savedLicenses).length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="no-saved-licenses">
                  No license numbers saved yet — they'll appear here after your first listing that includes one.
                </p>
              ) : (
                Object.entries(savedLicenses).map(([type, lic]) => (
                  <div
                    key={type}
                    className="flex items-center justify-between rounded-lg border p-3"
                    data-testid={`saved-license-row-${type}`}
                  >
                    <div>
                      <p className="text-sm font-medium">{type}</p>
                      <p className="text-sm text-gray-500" data-testid={`saved-license-number-${type}`}>{lic.number}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={forgettingType === type}
                      onClick={() => handleForgetLicense(type)}
                      data-testid={`forget-license-btn-${type}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {forgettingType === type ? 'Removing...' : 'Forget'}
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Store-wide Licenses & Certifications Card (iter-374) */}
          <StoreWideLicensesCard
            licenses={vendorLicenses}
            onAdd={addVendorLicenseRow}
            onUpdate={updateVendorLicense}
            onRemove={removeVendorLicenseRow}
            onSave={handleSaveVendorLicenses}
            saving={licensesSaving}
            uploadingIdx={uploadingLicenseIdx}
            onUploadImage={uploadVendorLicenseImage}
            onVerify={handleVerifyLicense}
            verifyingIdx={verifyingLicenseIdx}
            editable
          />

          {/* Delivery Management Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Delivery Management
              </CardTitle>
              <CardDescription>
                Set your default delivery mode for all listings. You can override this per-listing when creating or editing a listing.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="delivery_mode_default">Default Delivery Mode</Label>
                <Select
                  value={storeProfile.delivery_mode_default}
                  onValueChange={(val) => setStoreProfile({ ...storeProfile, delivery_mode_default: val })}
                >
                  <SelectTrigger id="delivery_mode_default" data-testid="delivery-mode-select">
                    <SelectValue placeholder="Select delivery mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="platform_managed">Platform Managed — Platform handles logistics</SelectItem>
                    <SelectItem value="self_managed">Self Managed — You handle all deliveries yourself</SelectItem>
                    <SelectItem value="hybrid">Hybrid — You manage delivery for specific listings</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Select <strong>Hybrid</strong> to enable the &quot;I manage my own deliveries&quot; override on individual listings.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* iter-325 — Shipping Management mode: read-only info card.
              SSOT lives in vendor_shipping_settings (managed on the
              dedicated Vendor Shipping Setup page), distinct from the
              per-listing "Default Delivery Mode" preference above. */}
          <Card data-testid="vendor-shipping-management-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Shipping Management
              </CardTitle>
              <CardDescription>
                How your orders are currently shipped — managed on the{' '}
                <a href="/vendor/shipping" className="underline text-blue-600">Vendor Shipping Setup</a> page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Badge
                  data-testid="vendor-shipping-management-badge"
                  className={shippingMode === 'self_managed' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}
                >
                  {shippingMode === 'self_managed' ? 'Self Managed' : 'Aniyor Managed'}
                </Badge>
                <p className="text-sm text-gray-500">
                  {shippingMode === 'self_managed'
                    ? 'You handle your own courier booking and delivery.'
                    : 'Aniyor books and manages delivery on your behalf.'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* iter-325 — Naming Policy acceptance toggle */}
          <Card data-testid="vendor-naming-policy-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Naming Policy
              </CardTitle>
              <CardDescription>
                Store names are leased and can be changed if a trademark owner makes a valid claim.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Badge className={namingPolicy.accepted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                    {namingPolicy.accepted ? 'Accepted' : 'Not Accepted'}
                  </Badge>
                  {namingPolicy.accepted && namingPolicy.accepted_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      Accepted on {new Date(namingPolicy.accepted_at).toLocaleString()}
                    </p>
                  )}
                </div>
                <Switch
                  data-testid="vendor-naming-policy-toggle"
                  checked={namingPolicy.accepted}
                  disabled={namingPolicySaving}
                  onCheckedChange={handleToggleNamingPolicy}
                />
              </div>
            </CardContent>
          </Card>

          {/* iter-231-K — Dispatch Time Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Dispatch Time
              </CardTitle>
              <CardDescription>
                How many business days you typically take to dispatch a product order BEFORE the courier picks it up. Customers see this on the PDP as <em>&quot;Dispatched in X–Y days, N days in transit&quot;</em>. Platform default is 1–2 days. Per-listing overrides are honored when set.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="processing_days_min">Minimum dispatch days</Label>
                  <Input
                    id="processing_days_min"
                    type="number"
                    min={0}
                    max={30}
                    inputMode="numeric"
                    value={storeProfile.processing_days_min}
                    onChange={(e) => setStoreProfile({ ...storeProfile, processing_days_min: e.target.value })}
                    placeholder="1"
                    data-testid="processing-days-min-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="processing_days_max">Maximum dispatch days</Label>
                  <Input
                    id="processing_days_max"
                    type="number"
                    min={0}
                    max={30}
                    inputMode="numeric"
                    value={storeProfile.processing_days_max}
                    onChange={(e) => setStoreProfile({ ...storeProfile, processing_days_max: e.target.value })}
                    placeholder="2"
                    data-testid="processing-days-max-input"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Leave blank to use the platform default (1–2 days). Only applied to product-type listings — services, subscriptions, bookings and events are unaffected.
              </p>
            </CardContent>
          </Card>

          {/* Tax & Compliance Info - Read Only */}
          {(registrationData.gstin || registrationData.pan_number) && (
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Tax & Compliance Information
                </CardTitle>
                <CardDescription>From your tax registration - Manage in Compliance & KYC</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {registrationData.gstin && (
                    <div className="p-3 bg-white rounded-lg border">
                      <Label className="text-xs text-gray-500">GSTIN</Label>
                      <p className="font-mono text-sm font-medium">{registrationData.gstin}</p>
                    </div>
                  )}
                  {registrationData.pan_number && (
                    <div className="p-3 bg-white rounded-lg border">
                      <Label className="text-xs text-gray-500">PAN</Label>
                      <p className="font-mono text-sm font-medium">{registrationData.pan_number}</p>
                    </div>
                  )}
                  {registrationData.gst_legal_name && (
                    <div className="p-3 bg-white rounded-lg border">
                      <Label className="text-xs text-gray-500">Legal Name</Label>
                      <p className="text-sm font-medium">{registrationData.gst_legal_name}</p>
                    </div>
                  )}
                  {registrationData.gst_state && (
                    <div className="p-3 bg-white rounded-lg border">
                      <Label className="text-xs text-gray-500">State</Label>
                      <p className="text-sm font-medium">{registrationData.gst_state}</p>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <Link to="/vendor/compliance">
                    <Button variant="outline" size="sm" type="button">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Manage Compliance & KYC
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bank Details - Read Only */}
          {registrationData.bank_account_number && (
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Bank Details
                  {registrationData.bank_verified && (
                    <Badge className="bg-green-500 text-white gap-1">
                      <Check className="h-3 w-3" /> Verified
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>For receiving payouts - Manage in Compliance & KYC</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-white rounded-lg border">
                    <Label className="text-xs text-gray-500">Bank Name</Label>
                    <p className="text-sm font-medium">{registrationData.bank_name || '-'}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <Label className="text-xs text-gray-500">Account Number</Label>
                    <p className="font-mono text-sm font-medium">{maskAccountNumber(registrationData.bank_account_number)}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <Label className="text-xs text-gray-500">IFSC Code</Label>
                    <p className="font-mono text-sm font-medium">{registrationData.bank_ifsc || '-'}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <Label className="text-xs text-gray-500">Account Holder</Label>
                    <p className="text-sm font-medium">{registrationData.bank_account_holder || '-'}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <Link to="/vendor/compliance">
                    <Button variant="outline" size="sm" type="button">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Update Bank Details
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* iter-231-S — Razorpay Route Linked Account card */}
          <VendorRazorpayRouteCard />

          {/* Brand Registry Prompt */}
          {!hasBlueTick && !hasGreenTick && (
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <strong>Get Verified!</strong> Protect your brand and build customer trust by applying for a 
                <Badge className="mx-1 bg-blue-500 text-white">Blue Tick</Badge> (Trademark) or 
                <Badge className="mx-1 bg-green-500 text-white">Green Tick</Badge> (Legacy Store) badge.
                <Link to="/vendor/brand-registry" className="ml-2 text-blue-600 underline font-medium">
                  Go to Brand Registry →
                </Link>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => window.history.back()}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default VendorStoreProfile;
